import useLangStore from "@/hooks/useLangStore";
import { Box, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useState } from "react";
import "./styles.css";

export interface SelectOption {
    value: string;
    label: string;
}

function getFormControlSx(dir: "rtl" | "ltr", active: boolean) {
    return {
        minWidth: "110px",
        direction: dir,
        "& .MuiInputLabel-root": {
            fontFamily: "Alexandria, sans-serif",
            fontSize: "13px",
            color: "var(--color-gray-02)",
            transformOrigin: dir === "rtl" ? "top right" : "top left",
            right: dir === "rtl" ? 0 : "unset",
            left: dir === "rtl" ? "unset" : 0,
            "&:not(.MuiInputLabel-shrink)": {
                transform: dir === "rtl" ? "translate(-14px, 8px) scale(1)" : "translate(14px, 8px) scale(1)",
            },
            "&.MuiInputLabel-shrink": {
                transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
            },
            "&.Mui-focused": {
                color: "var(--color-orange)",
            },
        },
        "& .MuiOutlinedInput-root": {
            height: "36px",
            fontFamily: "Alexandria, sans-serif",
            fontSize: "13px",
            color: "var(--color-dark)",
            borderRadius: "var(--radius-lev3)",
            backgroundColor: active ? "var(--color-orange-30)" : "var(--color-white)",
            "& fieldset": {
                borderColor: active ? "var(--color-orange)" : "var(--color-orange-30)",
                borderWidth: "1.5px",
            },
            "& fieldset legend": {
                textAlign: dir === "rtl" ? "right" : "left",
                marginLeft: dir === "rtl" ? "auto" : undefined,
                marginRight: dir === "rtl" ? "0px" : undefined,
            },
            "&:hover fieldset": {
                borderColor: "var(--color-orange)",
            },
            "&.Mui-focused fieldset": {
                borderColor: "var(--color-orange)",
                borderWidth: "1.5px",
            },
        },
        "& .MuiSelect-select": {
            display: "flex !important",
            alignItems: "center !important",
            paddingBlock: "0 !important",
            paddingInlineStart: "14px !important",
            paddingInlineEnd: "32px !important",
            height: "36px !important",
            minHeight: "unset !important",
            boxSizing: "border-box",
        },
        "& .MuiSelect-icon": {
            right: "unset",
            left: "unset",
            insetInlineEnd: "7px",
        },
    };
}

const getMenuProps = (dir: "rtl" | "ltr") => ({
    slotProps: {
        paper: {
            sx: {
                direction: dir,
                fontFamily: "Alexandria, sans-serif",
                borderRadius: "var(--radius-lev3)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                border: "1.5px solid var(--color-orange-30)",
                maxHeight: 240,
            },
        },
    },
});

const chipSx = {
    height: "20px",
    backgroundColor: "var(--color-orange-30)",
    color: "var(--color-orange)",
    border: "1px solid var(--color-orange)",
    fontFamily: "Alexandria, sans-serif",
    "& .MuiChip-label": { fontSize: "11px", fontWeight: 600, padding: "0 6px" },
};

const checkboxSx = {
    padding: "4px",
    color: "var(--color-gray-02)",
    "&.Mui-checked": { color: "var(--color-orange)" },
};

const menuItemSx = {
    fontFamily: "Alexandria, sans-serif",
    fontSize: "13px",
    minHeight: "36px",
    padding: "4px 10px",
};

// ── Multi-select ──────────────────────────────────────────────────────────────

interface FilterSelectProps {
    label: string;
    options: SelectOption[];
    value: string[];
    onChange: (val: string[]) => void;
}

export function FilterSelect({ label, options, value, onChange }: FilterSelectProps) {
    const { lang } = useLangStore();
    const dir = lang === "ar" ? "rtl" : "ltr";
    const active = value.length > 0;
    const [open, setOpen] = useState(false);
    const shrink = active || open;

    const getLabel = (v: string) => options.find((o) => o.value === v)?.label ?? v;

    return (
        <FormControl sx={getFormControlSx(dir, active)}>
            <InputLabel shrink={shrink}>{label}</InputLabel>
            <Select
                multiple
                value={value}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                onChange={(e) => onChange(e.target.value as string[])}
                input={<OutlinedInput notched={shrink} label={label} />}
                renderValue={(selected) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", overflow: "hidden", flexWrap: "nowrap" }}>
                        <Chip label={getLabel(selected[0])} size="small" sx={chipSx} />
                        {selected.length > 1 && <span className="filter-select-more">+{selected.length - 1}</span>}
                    </Box>
                )}
                MenuProps={getMenuProps(dir)}
            >
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={menuItemSx}>
                        <Checkbox checked={value.includes(opt.value)} sx={checkboxSx} size="small" />
                        <ListItemText
                            primary={opt.label}
                            slotProps={{ primary: { style: { fontFamily: "Alexandria, sans-serif", fontSize: "13px" } } }}
                        />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

// ── Boolean multi-select (Yes / No only — empty = all) ────────────────────────

interface FilterBoolProps {
    label: string;
    value: string[];
    onChange: (val: string[]) => void;
    yesLabel: string;
    noLabel: string;
}

export function FilterBool({ label, value, onChange, yesLabel, noLabel }: FilterBoolProps) {
    return (
        <FilterSelect
            label={label}
            options={[
                { value: "true", label: yesLabel },
                { value: "false", label: noLabel },
            ]}
            value={value}
            onChange={onChange}
        />
    );
}
